import { useEffect, useState } from 'react';

interface IndexInfo {
  name: string;
  displayName: string;
  category: string;
}

interface SearchModeConfig {
  mode: 'regular' | 'research';
  name: string;
  description: string;
  enabledIndexes: string[];
  lastUpdated: string;
}

interface AllSearchModesConfig {
  regular: SearchModeConfig;
  research: SearchModeConfig;
}

interface SearchModeIndexSelectorProps {
  onSave?: (config: AllSearchModesConfig) => Promise<void>;
  saving?: boolean;
}

export function SearchModeIndexSelector({ onSave, saving = false }: SearchModeIndexSelectorProps) {
  const [config, setConfig] = useState<AllSearchModesConfig | null>(null);
  const [availableIndexes, setAvailableIndexes] = useState<IndexInfo[]>([]);
  const [selectedMode, setSelectedMode] = useState<'regular' | 'research'>('regular');
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const [configRes, indexesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/search-modes`),
        fetch(`${API_BASE_URL}/api/search-modes/available-indexes`),
      ]);

      if (configRes.ok && indexesRes.ok) {
        const configData = await configRes.json();
        const indexesData = await indexesRes.json();

        setConfig(configData);
        setAvailableIndexes(indexesData.indexes);
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Failed to fetch search modes config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIndex = (indexName: string) => {
    if (!config) return;

    const modeConfig = config[selectedMode];
    const isEnabled = modeConfig.enabledIndexes.includes(indexName);

    const updatedIndexes = isEnabled
      ? modeConfig.enabledIndexes.filter((idx) => idx !== indexName)
      : [...modeConfig.enabledIndexes, indexName];

    setConfig({
      ...config,
      [selectedMode]: {
        ...modeConfig,
        enabledIndexes: updatedIndexes,
      },
    });
    setHasChanges(true);
  };

  const handleSelectAll = () => {
    if (!config) return;

    setConfig({
      ...config,
      [selectedMode]: {
        ...config[selectedMode],
        enabledIndexes: availableIndexes.map((idx) => idx.name),
      },
    });
    setHasChanges(true);
  };

  const handleDeselectAll = () => {
    if (!config) return;

    setConfig({
      ...config,
      [selectedMode]: {
        ...config[selectedMode],
        enabledIndexes: [],
      },
    });
    setHasChanges(true);
  };

  const handleSelectCategory = (category: string) => {
    if (!config) return;

    const categoryIndexes = availableIndexes.filter((idx) => idx.category === category).map((idx) => idx.name);

    const currentIndexes = config[selectedMode].enabledIndexes;
    const allCategoryEnabled = categoryIndexes.every((idx) => currentIndexes.includes(idx));

    const updatedIndexes = allCategoryEnabled
      ? currentIndexes.filter((idx) => !categoryIndexes.includes(idx))
      : [...new Set([...currentIndexes, ...categoryIndexes])];

    setConfig({
      ...config,
      [selectedMode]: {
        ...config[selectedMode],
        enabledIndexes: updatedIndexes,
      },
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (config && onSave) {
      await onSave(config);
      setHasChanges(false);
      // Refetch config to ensure UI is in sync with backend
      await fetchConfig();
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-zinc-600">Loading search modes configuration...</p>
      </div>
    );
  }

  if (!config) return null;

  // Group indexes by category
  const categories = [...new Set(availableIndexes.map((idx) => idx.category))];
  const modeConfig = config[selectedMode];
  const enabledCount = modeConfig.enabledIndexes.length;
  const totalCount = availableIndexes.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-zinc-900 mb-2">Search Mode Index Configuration</h2>
        <p className="text-sm text-zinc-600 mb-4">
          Control which content indexes are searchable in each mode. Users searching in a specific mode will only see
          results from enabled indexes.
        </p>
      </div>

      {/* Detailed Explanation */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-base font-semibold text-blue-900 mb-3">Understanding Search Modes</h3>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <span>🔍</span>
              <span>General Search</span>
            </h4>
            <p className="text-sm text-zinc-700 leading-relaxed">
              Designed for general users looking for any ASM content. This mode provides broad access to all content
              types including products, events, news, educational resources, and technical materials. Use this mode to
              ensure comprehensive search coverage across your entire content library.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <span>🔬</span>
              <span>Research Mode</span>
            </h4>
            <p className="text-sm text-zinc-700 leading-relaxed mb-2">
              Tailored specifically for academic researchers, engineers, and technical professionals. This mode
              prioritizes scholarly and technical content while filtering out commercial and promotional materials.
            </p>
            <p className="text-sm text-zinc-700 leading-relaxed">
              <strong className="text-blue-900">Recommended content types:</strong> Digital books and chapters,
              technical articles, conference proceedings, datasheets, materials databases, phase diagrams, and other
              academic resources. Consider excluding products, events, general news, and promotional content to maintain
              focus on research materials.
            </p>
          </div>
          <div className="border-t border-blue-200 pt-4 mt-4">
            <p className="text-xs text-zinc-600 italic">
              💡 <strong>Tip:</strong> Changes here affect both the search dropdown options and the actual search
              results. Disabled indexes will not appear in search results or facet filters for that mode.
            </p>
          </div>
        </div>
      </div>

      {/* Mode Selector */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setSelectedMode('regular')}
          className={`px-6 py-4 rounded-lg text-left transition-all ${
            selectedMode === 'regular'
              ? 'bg-zinc-900 text-white shadow-lg'
              : 'bg-white text-zinc-700 border-2 border-zinc-200 hover:border-zinc-300'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">🔍 General Search</h3>
            {selectedMode === 'regular' && (
              <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">Selected</span>
            )}
          </div>
          <p className="text-sm opacity-90">{config.regular.description}</p>
          <p className="text-xs mt-2 opacity-75">
            {config.regular.enabledIndexes.length} of {totalCount} indexes enabled
          </p>
        </button>

        <button
          onClick={() => setSelectedMode('research')}
          className={`px-6 py-4 rounded-lg text-left transition-all ${
            selectedMode === 'research'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-white text-zinc-700 border-2 border-zinc-200 hover:border-zinc-300'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">🔬 Research Mode</h3>
            {selectedMode === 'research' && (
              <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">Selected</span>
            )}
          </div>
          <p className="text-sm opacity-90">{config.research.description}</p>
          <p className="text-xs mt-2 opacity-75">
            {config.research.enabledIndexes.length} of {totalCount} indexes enabled
          </p>
        </button>
      </div>

      {/* Bulk Actions */}
      <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg border border-zinc-200">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-zinc-700">
            {enabledCount} of {totalCount} indexes enabled
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSelectAll}
            className="px-3 py-1.5 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded hover:bg-zinc-50 transition-colors"
          >
            Select All
          </button>
          <button
            onClick={handleDeselectAll}
            className="px-3 py-1.5 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded hover:bg-zinc-50 transition-colors"
          >
            Deselect All
          </button>
        </div>
      </div>

      {/* Index Selection by Category */}
      <div className="space-y-6">
        {categories.map((category) => {
          const categoryIndexes = availableIndexes.filter((idx) => idx.category === category);
          const enabledInCategory = categoryIndexes.filter((idx) =>
            modeConfig.enabledIndexes.includes(idx.name)
          ).length;
          const allEnabled = enabledInCategory === categoryIndexes.length;
          const someEnabled = enabledInCategory > 0 && enabledInCategory < categoryIndexes.length;

          return (
            <div key={category} className="bg-white border border-zinc-200 rounded-lg p-6">
              {/* Category Header */}
              <div
                className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-200 cursor-pointer"
                onClick={() => handleSelectCategory(category)}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={allEnabled}
                    ref={(input) => {
                      if (input) input.indeterminate = someEnabled;
                    }}
                    onChange={() => handleSelectCategory(category)}
                    className="w-5 h-5 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 cursor-pointer"
                  />
                  <h3 className="text-base font-semibold text-zinc-900">{category}</h3>
                  <span className="text-xs text-zinc-500">
                    ({enabledInCategory}/{categoryIndexes.length})
                  </span>
                </div>
              </div>

              {/* Category Indexes */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {categoryIndexes.map((index) => {
                  const isEnabled = modeConfig.enabledIndexes.includes(index.name);

                  return (
                    <label
                      key={index.name}
                      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        isEnabled
                          ? 'bg-zinc-50 border-2 border-zinc-900'
                          : 'bg-white border-2 border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => handleToggleIndex(index.name)}
                        className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">{index.displayName}</div>
                        <div className="text-xs text-zinc-500 font-mono truncate">{index.name}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-4 border-t border-zinc-200">
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          onClick={fetchConfig}
          disabled={saving}
          className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Reset Changes
        </button>

        {hasChanges && <span className="text-sm text-amber-600 ml-2">You have unsaved changes</span>}
      </div>

      {/* Last Updated */}
      {modeConfig.lastUpdated && (
        <div className="text-sm text-zinc-500 text-center">
          Last updated: {new Date(modeConfig.lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
}

export default SearchModeIndexSelector;
